import React, { useEffect, useState } from 'react';
import Slide, { SlideProps } from '@mui/material/Slide';
import { cloneDeep, isEmpty } from 'lodash';

import AlertDialog from '../../shared/components/AlertDialog';
import AuthService from '../../shared/services/AuthService';
import Box from '@mui/material/Box';
import Constants from '../constants';
import ContentAppBar from '../../shared/components/ContentAppBar';
import { CustomLoadingProgress } from '../../shared/components/CustomLoadingProgress';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Unstable_Grid2';
import LessonLaunchParams from '../models/LessonLaunchParams';
import LessonListPanel from './LessonListPanel';
import LessonService from '../services/LessonService';
import NoSearchResultsFound from '../../shared/components/NoSearchResultsFound';
import Pathway from '../models/Pathway';
import PathwayLessonPlayer from './PathwayLessonPlayer';
import PathwayLessonVm from '../models/LessonVm';
import ProgressStatusCode from '../models/ProgressStatusCode';
import RecentLessonPanel from './RecentLessonPanel';
import { SUPPORT_MESSAGE } from '../../shared/constants';
import SearchTextField from './SearchTextField';
import StatusPicker from './StatusPicker';
import styles from '../../shared/styles/CourseDialog.module.scss';
import { useLessonWindow } from '../hooks/useLessonWindow';
import usePathways from '../hooks/usePathways';

const Transition = React.forwardRef(
  (props: SlideProps, ref: React.Ref<unknown>) => (
    <Slide direction="right" ref={ref} {...props} />
  )
);

interface CourseDialogProps {
  open: boolean;
  pathway: Pathway;
  onClose: () => void;
}

function PathwayDialog(props: CourseDialogProps) {
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProgressStatusCode[]>([]);
  const [lessonLaunchParams, setLessonLaunchParams] =
    useState<LessonLaunchParams>(null);

  const userkey = AuthService.Userkey;

  const {
    getPathwayContent,
    pathwayCalled,
    pathwayLoading,
    pathwayError,
    pathwayData
  } = usePathways();

  const pathway = pathwayData?.pathwayContent;
  const lessons: PathwayLessonVm[] = LessonService.filterLessonsBy(
    cloneDeep(pathway?.lessons) ?? [],
    'pathway',
    searchText,
    statusFilter
  ) as PathwayLessonVm[];

  useEffect(() => {
    if (!props.pathway) {
      return;
    }

    if (!pathwayCalled) {
      getPathwayContent(userkey, props.pathway.pathkey, props.pathway.type);
    }
  }, [props.pathway]);

  const onSearch = (searchText: string) => setSearchText(searchText);
  const onFilter = (filterBy: ProgressStatusCode[]) => {
    setStatusFilter(filterBy);
  };
  const { handleMessage } = useLessonWindow(
    lessonLaunchParams?.lessonWindow,
    handleCloseMessage
  );

  const launchPathwayLesson = (
    lessonWindow: Window,
    title: string,
    zonekey: number,
    pathkey: string,
    lessonkey: string,
    resultkey: string
  ) => {
    console.debug(
      `Launching lesson with ${zonekey}, ${pathkey}, ${lessonkey}, ${resultkey}`
    );
    setLessonLaunchParams({
      lessonWindow,
      title,
      username: AuthService.Username,
      zonekey,
      pathkey,
      lessonkey,
      resultkey,
      targetOrigin: window.location.origin
    });
  };

  function handleCloseMessage(event?: MessageEvent) {
    if (!event || event.data === 'lessonComplete') {
      setLessonLaunchParams(null); // * close the lesson player
      getPathwayContent(userkey, props.pathway.pathkey, props.pathway.type);
    }
  }

  if (!pathwayLoading && pathwayError) {
    return (
      <AlertDialog
        title="Whoops!"
        message="There was a problem with loading lessons."
        details={SUPPORT_MESSAGE}
        onClose={props.onClose}
      />
    );
  }

  return (
    <>
      <Dialog
        fullScreen
        className={styles.courseDialog}
        open={props.open}
        TransitionComponent={Transition}
      >
        <ContentAppBar
          content={props.pathway}
          handleClose={props.onClose}
          disable={lessonLaunchParams !== null}
          label="PATHWAY"
        />
        <div className={styles.lessonListFrame}>
          {pathwayLoading && <CustomLoadingProgress />}
          <div className={styles.lessonListBody}>
            <Box m={1}>
              <RecentLessonPanel
                loading={pathwayLoading}
                contentType="pathway"
                pathwayLessons={pathway?.recentLessons ?? []}
                onLaunchPathwayLesson={launchPathwayLesson}
              />
            </Box>
            <Divider style={{ margin: '16px 0px' }} />
            <>
              <Box marginBottom={1}>
                <Grid container justifyContent="space-between">
                  <Grid>
                    <SearchTextField onSearch={onSearch} />
                  </Grid>
                  <Grid>
                    <StatusPicker
                      options={Constants.StatusFilterOptions}
                      onChange={onFilter}
                    />
                  </Grid>
                </Grid>
              </Box>
              {lessons.length === 0 &&
              (!!searchText || !isEmpty(statusFilter)) ? (
                <NoSearchResultsFound />
              ) : (
                <LessonListPanel
                  loading={pathwayLoading}
                  title={props.pathway.title}
                  contentType="pathway"
                  pathwayLessons={lessons}
                  filteredByStatus={!isEmpty(statusFilter)}
                  onLaunchPathwayLesson={launchPathwayLesson}
                />
              )}
            </>
          </div>
        </div>
      </Dialog>
      {lessonLaunchParams && (
        <PathwayLessonPlayer
          {...lessonLaunchParams}
          onMessage={handleMessage}
        />
      )}
    </>
  );
}

export default PathwayDialog;
